import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Container } from '../../layout/Container'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { ArrowRight, Play } from 'lucide-react'
import { BrainMockup } from './BrainMockup'
import { BuildMockup } from './BuildMockup'
import { GovernMockup } from './GovernMockup'

const mockupMap = {
  brain: BrainMockup,
  build: BuildMockup,
  govern: GovernMockup,
}

interface PlatformHeroProps {
  tKey: 'brain' | 'build' | 'govern'
}

export function PlatformHero({ tKey }: PlatformHeroProps) {
  const { t } = useTranslation()
  const Mockup = mockupMap[tKey]

  return (
    <section className="relative flex min-h-[85vh] items-center overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bg/50 to-bg" />

      <Container size="narrow" className="relative z-10 py-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Badge variant="accent">{t(`platformPages.${tKey}.badge`)}</Badge>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-6 text-4xl font-semibold tracking-tight text-text-primary sm:text-5xl md:text-6xl lg:text-7xl"
        >
          {t(`platformPages.${tKey}.headline`)}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mx-auto mt-6 max-w-2xl text-lg text-text-secondary md:text-xl"
        >
          {t(`platformPages.${tKey}.subheadline`)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Button asChild size="lg">
            <a href="/#diagnostico" className="group">
              {t('platformPages.common.ctaPrimary')}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
          </Button>
          <Button asChild variant="outline" size="lg">
            <a href="/#sistema" className="group">
              <Play className="mr-2 h-4 w-4" />
              {t('platformPages.common.ctaSecondary')}
            </a>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="relative mx-auto mt-16 max-w-4xl"
        >
          <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-accent/20 to-accent-light/20 blur-xl" />
          <div className="relative overflow-hidden rounded-2xl border border-border-subtle bg-surface-elevated p-2">
            <div className="aspect-[16/9] rounded-xl">
              <Mockup />
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  )
}
